import type Highcharts from 'highcharts';
import {
	buildComparisonStatusHtml,
	buildPreviousWithPercentHtml,
	COMPARISON_NEW_LABEL,
	resolveComparisonDisplayState,
} from './comparison-display-format.util';

export const CHART_LEGEND_SCALE_VAR = '--chart-legend-scale';
export const CHART_LEGEND_ITEM_WIDTH_VAR = '--chart-legend-item-width';

const LEGEND_SCALE = `var(${CHART_LEGEND_SCALE_VAR}, 1)`;
const LEGEND_ITEM_WIDTH = `var(${CHART_LEGEND_ITEM_WIDTH_VAR}, 100%)`;

/** Scales a pixel length for compact chart-card legends. */
export function legendScaledPx(px: number): string {
	return `calc(${px}px * ${LEGEND_SCALE})`;
}

/** Scales a theme font token (e.g. `--font-size-sm`) for legend HTML. */
export function legendScaledFont(cssVar: string): string {
	return `calc(var(${cssVar}) * ${LEGEND_SCALE})`;
}

/** Scales rem-based legend meta text. */
export function legendScaledRem(rem: number): string {
	return `calc(${rem}rem * ${LEGEND_SCALE})`;
}

/**
 * Derives a 0.65–1 scale from chart container size (compact dashboard cards).
 * Width drives horizontal legend fit; height caps vertical legend bulk.
 */
export function resolveChartLegendScale(
	containerWidth: number,
	containerHeight = containerWidth
): number {
	const widthScale =
		containerWidth <= 260
			? 0.72
			: containerWidth <= 320
				? 0.8
				: containerWidth <= 400
					? 0.9
					: containerWidth <= 520
						? 0.96
						: 1;

	const heightScale =
		containerHeight <= 160
			? 0.65
			: containerHeight <= 200
				? 0.78
				: containerHeight <= 260
					? 0.88
					: 1;

	return Math.min(widthScale, heightScale);
}

export interface ChartLegendLayout {
	width: number;
	itemWidth: number;
	itemDistance: number;
}

/** Derives legend width and per-column item width from the plot container. */
export function resolveChartLegendLayout(
	containerWidth: number,
	scale = 1
): ChartLegendLayout {
	const horizontalInset = 32;
	const width = Math.max(160, Math.round(containerWidth - horizontalInset));
	const itemDistance = Math.max(6, Math.round(12 * scale));

	let targetColumns = 1;
	if (width >= 820) {
		targetColumns = 4;
	} else if (width >= 560) {
		targetColumns = 3;
	} else if (width >= 360) {
		targetColumns = 2;
	}

	const gaps = itemDistance * Math.max(0, targetColumns - 1);
	const itemWidth = Math.max(128, Math.floor((width - gaps) / targetColumns));

	return { width, itemWidth, itemDistance };
}

function buildLegendLayoutChartUpdate(
	layout: ChartLegendLayout,
	scale: number
): Pick<Highcharts.Options, 'legend' | 'chart'> {
	return {
		legend: {
			width: layout.width,
			itemWidth: layout.itemWidth,
			itemDistance: layout.itemDistance,
			symbolPadding: Math.max(2, Math.round(4 * scale)),
		},
		chart: {
			spacing: [
				Math.max(4, Math.round(10 * scale)),
				16,
				Math.max(4, Math.round(8 * scale)),
				16,
			],
		},
	};
}

function syncChartLegendContainerLayout(container: HTMLElement): {
	scale: number;
	layout: ChartLegendLayout;
} {
	const scale = resolveChartLegendScale(
		container.clientWidth,
		container.clientHeight
	);
	const layout = resolveChartLegendLayout(container.clientWidth, scale);
	container.style.setProperty(CHART_LEGEND_SCALE_VAR, String(scale));
	container.style.setProperty(
		CHART_LEGEND_ITEM_WIDTH_VAR,
		`${layout.itemWidth}px`
	);
	return { scale, layout };
}

/** True when chart options intend to show a legend (default: enabled). */
export function isHighchartsLegendEnabled(
	legendOrChart:
		| Highcharts.Chart
		| Highcharts.Options
		| { legend?: Highcharts.LegendOptions }
		| undefined
): boolean {
	if (!legendOrChart) {
		return false;
	}
	const legendOptions =
		'options' in legendOrChart
			? (legendOrChart.options?.legend as
					| Highcharts.LegendOptions
					| undefined)
			: (legendOrChart as { legend?: Highcharts.LegendOptions }).legend;
	return legendOptions?.enabled !== false;
}

/** Hides leftover HTML/SVG legend nodes when legend is disabled. */
export function hideHighchartsLegendDom(
	chart: Highcharts.Chart | undefined
): void {
	if (!chart?.container) {
		return;
	}
	const legend = chart.legend as
		| (Highcharts.Legend & {
				group?: { hide?: () => void };
				legendGroup?: { hide?: () => void };
		  })
		| undefined;
	legend?.group?.hide?.();
	legend?.legendGroup?.hide?.();
	chart.container
		.querySelectorAll<HTMLElement>('.highcharts-legend')
		.forEach(node => {
			node.style.display = 'none';
		});
}

export function isLegendLayoutDisabled(
	options: Highcharts.Options | Highcharts.Chart | undefined
): boolean {
	if (!options) {
		return false;
	}
	const opts =
		'options' in options && options.options
			? options.options
			: (options as Highcharts.Options);
	const rootCustom = (opts as { custom?: { disableLegendLayout?: boolean } })
		.custom;
	const chartCustom = (
		opts.chart as { custom?: { disableLegendLayout?: boolean } } | undefined
	)?.custom;
	return (
		rootCustom?.disableLegendLayout === true ||
		chartCustom?.disableLegendLayout === true
	);
}

export function applyLegendLayoutToChartOptions(
	options: Highcharts.Options,
	container: HTMLElement
): Highcharts.Options {
	if (!isHighchartsLegendEnabled(options)) {
		return options;
	}
	if (isLegendLayoutDisabled(options)) {
		return options;
	}

	const { scale, layout } = syncChartLegendContainerLayout(container);
	const layoutPatch = buildLegendLayoutChartUpdate(layout, scale);
	const chartSpacing = options.chart?.spacing;
	const spacing = Array.isArray(chartSpacing) ? chartSpacing : undefined;

	return {
		...options,
		legend: {
			...(options.legend as Highcharts.LegendOptions),
			...layoutPatch.legend,
		},
		chart: {
			...(options.chart as Highcharts.ChartOptions),
			...layoutPatch.chart,
			spacing: spacing
				? [
						layoutPatch.chart!.spacing![0],
						spacing[1] ?? 16,
						layoutPatch.chart!.spacing![2],
						spacing[3] ?? 16,
					]
				: layoutPatch.chart!.spacing,
		},
	};
}

/** Highcharts exposes `legend.render()` at runtime; typings omit it. */
export function rerenderHighchartsLegend(
	chart: Highcharts.Chart | undefined
): void {
	if (!isHighchartsLegendEnabled(chart)) {
		hideHighchartsLegendDom(chart);
		return;
	}
	const legend = chart?.legend as
		| (Highcharts.Legend & { render?: () => void })
		| undefined;
	if (!legend || typeof legend.render !== 'function') {
		return;
	}
	// Must call as a method — detached `render?.()` loses `this` and throws
	// "Cannot read properties of undefined (reading 'chart')" inside Highcharts.
	legend.render();
}

/**
 * True when the chart is still mounted and not destroyed (Highcharts clears
 * `options` on destroy; `container.isConnected` catches DOM removal).
 */
export function isHighchartsChartAlive(
	chart: Highcharts.Chart | undefined
): boolean {
	return chart?.container?.isConnected === true && chart.options != null;
}

/** Sets legend CSS vars on the chart container and updates Highcharts legend layout. */
export function applyChartLegendScale(
	container: HTMLElement | null | undefined,
	chart?: Highcharts.Chart
): number {
	if (!container) {
		return 1;
	}

	if (chart && isLegendLayoutDisabled(chart)) {
		return resolveChartLegendScale(
			container.clientWidth,
			container.clientHeight
		);
	}

	const { scale, layout } = syncChartLegendContainerLayout(container);
	const layoutKey = `${container.clientWidth}x${container.clientHeight}:${scale}`;
	const previousLayoutKey = container.dataset.chartLegendLayoutKey ?? '';

	if (
		chart &&
		isHighchartsChartAlive(chart) &&
		!isLegendLayoutDisabled(chart) &&
		isHighchartsLegendEnabled(chart) &&
		layoutKey !== previousLayoutKey &&
		typeof chart.update === 'function'
	) {
		container.dataset.chartLegendLayoutKey = layoutKey;
		chart.update(buildLegendLayoutChartUpdate(layout, scale), false);
		rerenderHighchartsLegend(chart);
	}

	return scale;
}

export const CHART_LEGEND_NO_DATA_LABEL = 'No data';
export const CHART_LEGEND_NO_PREVIOUS_DATA_LABEL = `0 (${COMPARISON_NEW_LABEL})`;

/** True when the API sent a finite aggregation number (0 is valid data). */
export function hasLegendAggregationValue(
	value: number | null | undefined
): value is number {
	return typeof value === 'number' && Number.isFinite(value);
}

export interface LegendSeriesComparisonProbe {
	comparisonAggregationValue?: number | null;
	comparisonData?: unknown[] | null;
}

/** True when a series includes previous-period legend data. */
export function resolveLegendSeriesUserOptions(series: {
	userOptions?: LegendSeriesComparisonProbe & {
		aggregationValue?: number;
	};
	options?: LegendSeriesComparisonProbe & { aggregationValue?: number };
}): LegendSeriesComparisonProbe & { aggregationValue?: number } {
	return { ...series.options, ...series.userOptions };
}

export function seriesHasComparisonLegendData(
	series: LegendSeriesComparisonProbe | undefined
): boolean {
	if (!series) {
		return false;
	}
	if (hasLegendAggregationValue(series.comparisonAggregationValue)) {
		return true;
	}
	return (
		Array.isArray(series.comparisonData) && series.comparisonData.length > 0
	);
}

/** True when any series on the chart has comparison data — drives a second legend row. */
export function chartHasComparisonLegendRows(
	chartSeries:
		| Array<{
				userOptions?: LegendSeriesComparisonProbe;
				options?: LegendSeriesComparisonProbe;
		  }>
		| undefined
): boolean {
	return (chartSeries ?? []).some(series =>
		seriesHasComparisonLegendData(resolveLegendSeriesUserOptions(series))
	);
}

function formatLegendAggregationValue(
	value: number | null | undefined,
	formatNumber: (value: number) => string
): { text: string; isMissing: boolean } {
	if (hasLegendAggregationValue(value)) {
		return { text: formatNumber(value), isMissing: false };
	}
	return { text: CHART_LEGEND_NO_DATA_LABEL, isMissing: true };
}

export interface CartesianAggregationLegendLabelParams {
	seriesName: string;
	swatchInlineStyle: string;
	aggregationValue?: number;
	comparisonAggregationValue?: number;
	comparisonData?: unknown[] | null;
	formatNumber: (value: number) => string;
	fontFamily?: string;
	/** When true, reserve a second row for comparison or a no-previous-data placeholder. */
	comparisonRowEnabled?: boolean;
}

/** HTML legend row used by line, area, bar, and column charts. */
export function buildCartesianAggregationLegendLabelHtml(
	params: CartesianAggregationLegendLabelParams
): string {
	const {
		seriesName,
		swatchInlineStyle,
		aggregationValue,
		comparisonAggregationValue,
		comparisonData,
		formatNumber,
		fontFamily,
		comparisonRowEnabled = false,
	} = params;

	const fontFamilyStyle = fontFamily ? `font-family: ${fontFamily};` : '';
	const currentInput = hasLegendAggregationValue(aggregationValue)
		? aggregationValue
		: null;
	const previousInput = hasLegendAggregationValue(comparisonAggregationValue)
		? comparisonAggregationValue
		: seriesHasComparisonLegendData({
					comparisonAggregationValue,
					comparisonData,
			  })
			? 0
			: null;
	const hasSeriesComparison =
		hasLegendAggregationValue(comparisonAggregationValue) ||
		seriesHasComparisonLegendData({
			comparisonAggregationValue,
			comparisonData,
		});
	const comparisonState =
		comparisonRowEnabled || hasSeriesComparison
			? resolveComparisonDisplayState(currentInput, previousInput)
			: null;

	const { text: valueText, isMissing: isValueMissing } =
		comparisonState != null
			? {
					text: formatNumber(comparisonState.displayCurrent),
					isMissing: comparisonState.kind === 'no-data',
				}
			: formatLegendAggregationValue(aggregationValue, formatNumber);
	const valueColor = isValueMissing
		? 'var(--text-secondary-color)'
		: 'var(--text-primary-color)';
	const valueWeight = isValueMissing
		? 'var(--font-weight-regular)'
		: 'var(--font-weight-medium)';

	let comparisonBlock = '';

		if (comparisonState?.kind === 'new' && comparisonRowEnabled) {
			comparisonBlock = `
	               <div style="margin-top: ${legendScaledPx(2)};">
	                   ${buildComparisonStatusHtml('new', { fontFamilyStyle })}
	               </div>`;
		} else if (comparisonState?.kind === 'no-data' && comparisonRowEnabled) {
			comparisonBlock = `
	               <div style="margin-top: ${legendScaledPx(2)};">
	                   ${buildComparisonStatusHtml('no-data', { fontFamilyStyle })}
	               </div>`;
		} else if (
			comparisonState != null &&
			comparisonState.percentChange != null
		) {
			comparisonBlock = `
	               <div style="display: flex; align-items: center; gap: ${legendScaledPx(8)}; margin-top: ${legendScaledPx(2)};">
	                   ${buildPreviousWithPercentHtml({
							previousFormatted: formatNumber(
								comparisonState.displayPrevious
							),
							percentChange: comparisonState.percentChange,
							decimals: 0,
							fontFamilyStyle,
						})}
	               </div>`;
		}

	const hasSecondRow = comparisonRowEnabled || Boolean(comparisonBlock);

	return `
           <div style="display: flex; align-items: stretch; margin-bottom: ${legendScaledPx(6)}; max-width: ${LEGEND_ITEM_WIDTH}; width: 100%; box-sizing: border-box; ${fontFamilyStyle}">
               <div style="width: ${legendScaledPx(3)}; min-height: ${legendScaledPx(hasSecondRow ? 28 : 20)}; align-self: stretch; ${swatchInlineStyle}; margin-right: ${legendScaledPx(8)}; flex-shrink: 0; border-radius: 1px;"></div>
               <div style="display: flex; flex-direction: column; min-width: 0;">
                   <div style="display: flex; align-items: baseline; gap: ${legendScaledPx(8)}; min-width: 0;">
                       <span style="color: var(--text-secondary-color); font-size: ${legendScaledFont('--font-size-xs')}; font-weight: var(--font-weight-regular); letter-spacing: -0.01em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; flex: 1 1 auto; ${fontFamilyStyle}">${seriesName}</span>
                       <span style="color: ${valueColor}; font-weight: ${valueWeight}; font-size: ${legendScaledFont('--font-size-sm')}; line-height: 1; flex-shrink: 0; letter-spacing: -0.02em; white-space: nowrap; ${fontFamilyStyle}">
                           ${valueText}
                       </span>
                   </div>
                   ${comparisonBlock}
               </div>
           </div>
       `;
}

export interface SimpleMetricLegendLabelParams {
	seriesName: string;
	color: string;
	primaryText: string;
	secondaryText?: string;
}

/** Compact metric legend row (donut, scatter). */
export function buildSimpleMetricLegendLabelHtml(
	params: SimpleMetricLegendLabelParams
): string {
	const { seriesName, color, primaryText, secondaryText } = params;
	const secondaryBlock = secondaryText
		? `<span style="color: var(--text-secondary-color); font-size: ${legendScaledFont('--font-size-xs')}; font-weight: var(--font-weight-regular);">
                  ${secondaryText}
                </span>`
		: '';

	return `
        <div style="display: flex; margin-bottom: ${legendScaledPx(16)}; width: 100%;">
          <div style="display: flex">
            <div style="width: ${legendScaledPx(3)}; height: 100%; background-color: ${color}; margin-right: ${legendScaledPx(12)}; border-radius: 2px;"></div>
            <div style="display: flex; flex-direction: column;">
              <span style="color: var(--text-secondary-color); font-size: ${legendScaledFont('--font-size-sm')}; font-weight: var(--font-weight-regular); margin-bottom: ${legendScaledPx(4)}; letter-spacing: -0.01em;">
                ${seriesName}
              </span>
              <div style="display: flex; align-items: baseline; gap: ${legendScaledPx(8)};">
                <span style="color: var(--text-primary-color); font-weight: var(--font-weight-light); font-size: ${legendScaledFont('--font-size-xl')}; line-height: 1; text-align: left; letter-spacing: -0.02em;">
                  ${primaryText}
                </span>
                ${secondaryBlock}
              </div>
            </div>
          </div>
        </div>
      `;
}
